import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Input from '../input/Input';
import { CheckboxGroup } from '../checkbox/Checkbox';
import { RadioGroup } from '../radioInput/RadioInput';
import Select from '../select/Select';
import Button from '../button/Button';
import * as classes from './SmeLendingForm.module.scss';

const questions = {
  cbilsBblsOrRls:
    'Did you take these out under the Coronavirus Business Interruption Loan Scheme (CBILS), the Bounce Back Loan Scheme (BBLS) or the Recovery Loan Scheme (RLS)? If yes, check all that apply:'
};

const createValidator = () => {
  return yup.object().shape({
    turnover: yup.string().oneOf(['50000', '99999', '299999', '499999', '999999', '1000000']),
    existingLending: yup.string().typeError('Please select an answer').oneOf(['Yes', 'No']),
    existingOverdraft: yup.object().when('existingLending', {
      is: 'Yes',
      then: yup.object().shape({
        overdraftLimit: yup.number().default(0),
        overdraftUsage: yup.number().typeError('Please enter a valid amount').required()
      })
    })
  });
};

function Step5({ onSubmit }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    control
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(createValidator()),
    shouldUnregister: true
  });

  const existingLending = watch('existingLending');

  return (
    <form
      className={classes.step}
      onSubmit={handleSubmit(({ cbilsBblsOrRls, existingLending, ...data }) =>
        onSubmit(
          {
            ...data,
            filterQuestions: cbilsBblsOrRls?.length
              ? [
                  {
                    question: questions.cbilsBblsOrRls,
                    answer: cbilsBblsOrRls.join(', ')
                  }
                ]
              : []
          },
          6
        )
      )}
    >
      <Controller
        control={control}
        name="turnover"
        defaultValue="50000"
        render={({ field }) => (
          <Select
            value={field.value}
            onChange={field.onChange}
            label="What is your current annual business turnover - based on the most recent information available?"
            error={errors.turnover}
            options={{
              50000: 'Under £50,000',
              99999: '£50,000 - £99,999',
              299999: '£100,000 - £299,999',
              499999: '£300,000 - £499,999',
              999999: '£500,000 - £999,999',
              1000000: '£1,000,000+'
            }}
          />
        )}
      />

      <RadioGroup
        label="Do you have any existing business loans or business overdrafts?"
        error={errors.existingLending}
        options={[
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' }
        ]}
        {...register('existingLending')}
      />

      {existingLending === 'Yes' ? (
        <Fragment>
          <Input
            id="overdraftUsage"
            type="number"
            prefix="£"
            label="What are the outstanding balances?"
            error={errors.existingOverdraft?.overdraftUsage}
            {...register('existingOverdraft.overdraftUsage')}
          />

          <CheckboxGroup
            label={questions.cbilsBblsOrRls}
            error={errors.cbilsBblsOrRls}
            options={[
              { id: 'cbils', value: 'CBILS', label: 'CBILS' },
              { id: 'bbls', value: 'BBLS', label: 'BBLS' },
              { id: 'rls', value: 'RLS', label: 'RLS' }
            ]}
            {...register('cbilsBblsOrRls')}
          />
        </Fragment>
      ) : null}

      <Button variant="secondary">Next</Button>
    </form>
  );
}
Step5.propTypes = {
  onSubmit: PropTypes.func
};

export default Step5;
